import React, {FunctionComponent, lazy, Suspense} from 'react'
import Header from '../../navigation/Header/Header'
import Left from '../../navigation/Left/Left'
import {Redirect, Route, Switch} from 'react-router'
import {ROUTES} from '../../../http/routes'
import {NoMatch} from '../etc/NoMatch'
import styles from './Page.module.sass'
import Breadcrumbs from '../../navigation/Breadcrumbs/Breadcrumbs'
import Gallery from '../../etc/Gallery/Gallery'
import {EventFrame, UnreadFrame, UpdateFrame, UserFrame, UserInfoFrame} from '../../../responses/events'
import {eventActions} from '../../../redux/global/events'
import {useAllPermissions, useRootDispatch, useRootSelector, useSocket} from '../../../utils/hooks'
import {notificationActions} from '../../../redux/global/notification'
import ProfilePage from '../settings/ProfilePage/ProfilePage'
import {componentToast} from '../../controls/Toaster/Toaster'
import {userActions} from '../../../redux/global/user/user-actions'
import {NotificationType, PushNotification} from '../../../responses/notification'
import NotificationElement, {historyDataToUrl} from '../../data-components/elements/NotificationElement/NotificationElement'
import DialogsController from '../etc/DialogsController'
import {systemActions} from '../../../redux/global/system'
import {usersActions} from '../../../redux/data/users'
import {useQueries} from '../../../utils/hooks/useQueries'
import ProjectsPage from '../projects/ProjectsPage'
import {flow} from 'fp-ts/es6/function'
import ProtectedRoute from '../../etc/ProtectedRoute'
import {PERMISSIONS} from '../../../constants/permissions'
import {push} from 'connected-react-router'
import TestPage from '../TestPage'
import PricePage from '../price/PricePage'
import ContactsPage from '../contacts/ContactsPage'

const SOURCE_UPDATE_CHANNEL = 'source.update'
const USER_INFO_CHANNEL = 'user.info'

const StatisticPage = lazy(() => import('../statistics/StatisticsPage'))
const SettingsPage = lazy(() => import('../settings/SettingsPage/SettingsPage'))
const TasksPage = lazy(() => import('../tasks/TasksPage'))
const SearchPage = lazy(() => import('../search/SearchPage'))
const ArchivePage = lazy(() => import('../archive/ArchivePage/ArchivePage'))

const Page: FunctionComponent = () => {
    const dispatch = useRootDispatch()
    const user = useRootSelector(state => state.global.user.data)
    const {isMobile} = useQueries()

    const canViewSettings = useAllPermissions([PERMISSIONS.SETTINGS.ACCESS])

    useSocket<UserFrame, PushNotification>(`user.${user?.id}`, '.update',
        [user?.id, user?.notification_method_settings],
        frame => {
            dispatch(userActions.update.success(frame.user))
            frame.reload && dispatch(userActions.reload())
        },
        frame => {
            dispatch(notificationActions.notify(frame))
            user?.notification_method_settings?.popup && componentToast({
                content: <NotificationElement
                    type={'push'}
                    notification={frame}
                />,
                onClick: () => {
                    switch (frame.type) {
                        case NotificationType.SYSTEM_MESSAGE:
                            frame.data.url && dispatch(push(frame.data.url))
                            break
                        case NotificationType.HISTORY_CHANGE:
                            dispatch(push(historyDataToUrl(frame.data)))
                            break
                    }
                }
            })
        }
    )
    useSocket<EventFrame>(`user.${user?.id}.role.${user?.role?.id}.projects`, '.update',
        [user?.id, user?.role?.id],
        frame => dispatch(eventActions.event(frame.event))
    )
    useSocket<UnreadFrame>(
        `user.${user?.id}.unread`, '.update', [user?.id],
        flow(notificationActions.event, dispatch)
    )
    useSocket<UpdateFrame>(SOURCE_UPDATE_CHANNEL, '.start', [],
        frame => dispatch(systemActions.startUpdate(frame.update_at))
    )
    useSocket<UpdateFrame>(SOURCE_UPDATE_CHANNEL, '.finish', [],
        frame => dispatch(systemActions.finishUpdate(frame.update_at))
    )
    useSocket<UserInfoFrame>(USER_INFO_CHANNEL, '.update', [],
        frame => dispatch(usersActions.update(frame.users))
    )

    return <div className={styles.page}>
        <DialogsController/>
        <Header className={styles.header}/>
        {
            !isMobile && <>
                <Left className={styles.left}/>
                <Breadcrumbs className={styles.breadcrumbs}/>
            </>
        }
        <main>
            <Suspense fallback={<div/>}>
                <Switch>
                    <Route exact path={ROUTES.ROOT} render={() => <Redirect to={ROUTES.PROJECTS}/>}/>
                    <Route path={ROUTES.PROJECTS} component={ProjectsPage}/>
                    <Route path={ROUTES.STATISTICS} component={StatisticPage}/>
                    <Route path={ROUTES.TASKS} component={TasksPage}/>
                    <Route path={ROUTES.PROFILE} component={ProfilePage}/>
                    <Route path={ROUTES.ARCHIVE} component={ArchivePage}/>
                    <Route exact path={ROUTES.SETTINGS} render={() => <Redirect to={ROUTES.SETTINGS + '/country'}/>}/>
                    <ProtectedRoute path={ROUTES.SETTINGS} component={SettingsPage} rules={[{
                        check: () => canViewSettings,
                        redirect: ROUTES.ROOT,
                    }]}/>
                    <Route path={ROUTES.SEARCH} component={SearchPage}/>
                    <Route path={ROUTES.PRICE} component={PricePage}/>
                    <Route path={ROUTES.CONTACTS} component={ContactsPage}/>
                    <Route path={ROUTES.TEST} component={TestPage}/>
                    <Route component={NoMatch}/>
                </Switch>
                <Gallery/>
            </Suspense>
        </main>
    </div>
}

export default Page
